export function showSlider(
  target = '.farlo-ui-list-shows__shows',
  options = {},
  buttonPosition = 'below'
) {
  const slider = typeof target === 'string' ? document.querySelector(target) : target;
  if (!slider) return null;

  // Helpers
  const $cells = () => Array.from(slider.querySelectorAll('.farlo-ui-list-shows__show'));
  const raf = (fn) => requestAnimationFrame(() => requestAnimationFrame(fn)); // 2x raf after layout
  const debounce = (fn, ms = 100) => {
    let t; return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
  };

  // Read per-element options from HTML (data-flickity='{"prevNextButtons":false,...}')
  let htmlOptions = {};
  const attr = slider.getAttribute('data-flickity');
  if (attr) {
    try { htmlOptions = JSON.parse(attr); } catch (_) { /* ignore bad JSON */ }
  }

  // Base defaults (kept close to your originals)
  const defaultOptions = {
    cellAlign: 'left',
    contain: true,
    pageDots: false,
    wrapAround: false,
    prevNextButtons: true,
    adaptiveHeight: false,   // keep false; we’re equalizing all cells instead
    freeScroll: true,
    draggable: true,
    imagesLoaded: true
  };

  // Merge: defaults < HTML < function-call
  const merged = { ...defaultOptions, ...htmlOptions, ...options };

  // Equalize heights across all cells
  const syncCardHeights = (flkty) => {
    const cells = $cells();
    if (!cells.length) return;

    // Reset then measure
    cells.forEach(c => { c.style.minHeight = ''; });
    // Use offsetHeight to include padding/border; relies on same box model
    let maxH = 0;
    cells.forEach(c => { const h = c.offsetHeight; if (h > maxH) maxH = h; });

    if (maxH > 0) cells.forEach(c => { c.style.minHeight = `${maxH}px`; });

    // Ensure Flickity recalculates size after height change
    if (flkty) { flkty.resize(); flkty.reposition(); }
  };

  // Helper to show/hide buttons & manage the "below" wrapper
  const toggleButtons = (flkty, wantVisible) => {
    const prevBtn = flkty.element.querySelector('.flickity-button.previous');
    const nextBtn = flkty.element.querySelector('.flickity-button.next');

    [prevBtn, nextBtn].forEach(btn => {
      if (!btn) return;
      btn.style.display = wantVisible ? '' : 'none';
      btn.setAttribute('tabindex', wantVisible ? '0' : '-1');
      btn.setAttribute('aria-hidden', wantVisible ? 'false' : 'true');
    });

    const existingWrapper = flkty.element.querySelector('.site__wrapper');
    if (wantVisible && buttonPosition === 'below' && prevBtn && nextBtn) {
      if (!existingWrapper) {
        const wrapper = document.createElement('div');
        wrapper.className = 'site__wrapper';
        prevBtn.parentElement.insertBefore(wrapper, prevBtn);
        wrapper.appendChild(prevBtn);
        wrapper.appendChild(nextBtn);
      }
    } else if (existingWrapper) {
      const parent = existingWrapper.parentElement;
      const buttons = Array.from(existingWrapper.querySelectorAll('.flickity-button'));
      buttons.forEach(btn => parent.insertBefore(btn, existingWrapper));
      existingWrapper.remove();
    }
  };

  // If there is an existing instance, update options and UI
  const existing = Flickity.data(slider);
  if (existing) {
    existing.options = {
      ...existing.options,
      ...htmlOptions,
      ...options,
      cellAlign: 'left',
      contain: true,
      pageDots: false
    };

    toggleButtons(existing, !!existing.options.prevNextButtons);

    // Re-equalize after any option changes
    raf(() => syncCardHeights(existing));

    existing.resize();
    existing.reposition();
    return existing;
  }

  // Fresh init
  const flkty = new Flickity(slider, merged);

  // Respect prevNextButtons immediately
  toggleButtons(flkty, !!merged.prevNextButtons);

  // Initial equalization after layout tick(s)
  raf(() => syncCardHeights(flkty));

  // After fonts load, sizes can change → re-equalize
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => raf(() => syncCardHeights(flkty)));
  }

  // After images load, sizes can change → re-equalize
  if (merged.imagesLoaded) {
    flkty.on('imagesLoaded', () => raf(() => syncCardHeights(flkty)));
  }

  // Also equalize when Flickity is ready / resized / cells change
  flkty.on('ready', () => raf(() => syncCardHeights(flkty)));
  flkty.on('resize', () => raf(() => syncCardHeights(flkty)));
  flkty.on('cellAdded', () => raf(() => syncCardHeights(flkty)));
  flkty.on('settle', () => raf(() => syncCardHeights(flkty)));

  // Your existing layout stabilizers
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => { flkty.resize(); flkty.reposition(); });
  }
  requestAnimationFrame(() => { flkty.resize(); flkty.reposition(); });

  // ResizeObserver on slider (content shifts) → re-equalize
  const ro = new ResizeObserver(debounce(() => {
    flkty.resize();
    flkty.reposition();
    syncCardHeights(flkty);
  }, 120));
  ro.observe(slider);

  let velocity = 0;
  let rafId = null;

  const FRICTION = 0.6;
  const DELTA_SCALE = 0.8;
  const HORIZ_BIAS = 1.2;

  function clampX(x) {
    if (flkty.options.wrapAround) return x;
    const maxNegShift = Math.min(0, flkty.size.innerWidth - flkty.slideableWidth);
    if (x > 0) return 0;
    if (x < maxNegShift) return maxNegShift;
    return x;
  }

  function animate() {
    if (Math.abs(velocity) < 0.02) {
      velocity = 0;
      rafId = null;
      return;
    }
    flkty.x = clampX(flkty.x - velocity);
    flkty.positionSlider();
    flkty.updateSelectedSlide();
    velocity *= FRICTION;
    rafId = requestAnimationFrame(animate);
  }

  const onWheel = (event) => {
    let dx = event.deltaX;
    const dy = event.deltaY;

    const isHorizontalGesture = event.shiftKey || Math.abs(dx) > Math.abs(dy) * HORIZ_BIAS;
    if (!isHorizontalGesture) return;

    if (event.shiftKey && Math.abs(dx) < 1 && Math.abs(dy) >= 1) dx = dy;

    const predictedVelocity = velocity + dx * DELTA_SCALE;
    const predictedX = clampX(flkty.x - predictedVelocity);
    const cannotMove = predictedX === flkty.x && Math.abs(predictedVelocity) < 0.01;
    if (cannotMove) return;

    event.preventDefault();
    velocity += dx * DELTA_SCALE;
    if (!rafId) animate();
  };

  slider.addEventListener('wheel', onWheel, { passive: false });

  const onResize = debounce(() => {
    flkty.x = clampX(flkty.x);
    flkty.positionSlider();
    flkty.updateSelectedSlide();
    syncCardHeights(flkty);
  }, 120);
  window.addEventListener('resize', onResize);

  flkty.on('destroy', () => {
    slider.removeEventListener('wheel', onWheel);
    window.removeEventListener('resize', onResize);
    ro.disconnect();
    if (rafId) cancelAnimationFrame(rafId);
    // Clean up equalization if needed:
    $cells().forEach(c => { c.style.minHeight = ''; });
  });

  return flkty;
}
